// Iter 81 / Phase E0 — Employee create + edit form.
// Single component covers both create (no :id) and edit (with :id).
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const EMPTY = {
    full_name: "",
    personal_email: "",
    official_email: "",
    mobile: "",
    alternate_mobile: "",
    dob: "",
    gender: "",
    pan: "",
    aadhaar: "",
    employment_category: "Employee",
    department: "",
    designation: "",
    date_of_joining: "",
    confirmation_date: "",
    probation_end_date: "",
    notice_period_days: 30,
    status: "Draft",
    reporting_manager_employee_id: "",
    current_address: "",
    permanent_address: "",
    emergency_contact: { name: "", number: "", relationship: "" },
    bank_name: "",
    bank_account_number: "",
    bank_ifsc: "",
    bank_beneficiary_name: "",
    notes: "",
    // Iter 82 / Phase E1.0 — Leave Management linkage.
    holiday_calendar_id: "",
    leave_workflow_code: "",
    // Iter 98 / Phase E2.4 — per-employee attendance source override.
    // Null = use the resolved AttendancePolicy.attendance_source_mode.
    attendance_source_override: "",
};

const Section = ({ title, children }) => (
    <section className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </section>
);

const Field = ({ label, children, required, hint, testId }) => (
    <div className="space-y-1" data-testid={testId}>
        <Label className="text-xs font-medium text-slate-700">
            {label} {required && <span className="text-rose-500">*</span>}
        </Label>
        {children}
        {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
);

export default function EmployeeForm() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const { can } = useAuth();
    const [form, setForm] = useState(EMPTY);
    const [meta, setMeta] = useState({ departments: [], designations: [], employment_categories: [], employee_statuses: [], genders: [] });
    const [managers, setManagers] = useState([]);
    const [holidayCalendars, setHolidayCalendars] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    // PFD-ONBOARDING-002 · legacy create requires explicit shell intent.
    const [ackNoEss, setAckNoEss] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/hr/employee/meta"),
            api.get("/hr/employee/list", { params: { page_size: 200, sort: "name_asc" } }),
            api.get("/leave/holiday-calendar/list").catch(() => ({ data: { items: [] } })),
        ])
            .then(([metaRes, listRes, calRes]) => {
                setMeta(metaRes.data);
                setManagers(listRes.data.items || []);
                setHolidayCalendars(calRes.data.items || []);
            })
            .catch((err) => toast.error(formatApiError(err, "Failed to load form data")));
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        api.get(`/hr/employee/${id}`)
            .then(({ data }) => {
                if (cancelled) return;
                setForm({
                    ...EMPTY,
                    ...data,
                    emergency_contact: data.emergency_contact || EMPTY.emergency_contact,
                    reporting_manager_employee_id: data.reporting_manager_employee_id || "",
                });
            })
            .catch((err) => toast.error(formatApiError(err, "Failed to load employee")))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [id, isEdit]);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const setEmergency = (key, value) =>
        setForm((f) => ({ ...f, emergency_contact: { ...f.emergency_contact, [key]: value } }));

    const filteredManagers = useMemo(
        () => managers.filter((m) => m.id !== id),
        [managers, id]
    );

    const submit = async () => {
        if (!form.full_name?.trim()) {
            toast.error("Full name is required.");
            return;
        }
        if (!isEdit && !ackNoEss) {
            toast.error("Please explicitly acknowledge employee-only shell creation (no user account, no ESS access, no leave balances) — or use Unified Onboarding for login-enabled hires.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                notice_period_days: parseInt(form.notice_period_days || 0, 10),
                dob: form.dob || null,
                date_of_joining: form.date_of_joining || null,
                confirmation_date: form.confirmation_date || null,
                probation_end_date: form.probation_end_date || null,
                reporting_manager_employee_id: form.reporting_manager_employee_id || null,
                // Iter 98 / Phase E2.4 — empty string ≠ valid Literal;
                // coerce to null so backend uses the policy default.
                attendance_source_override: form.attendance_source_override || null,
                // PFD-ONBOARDING-002 — explicit operator intent, only on create.
                ...(isEdit ? {} : { acknowledge_no_ess_access: true }),
            };
            const res = isEdit
                ? await api.patch(`/hr/employee/${id}`, payload)
                : await api.post(`/hr/employee`, payload);
            toast.success(isEdit ? "Employee updated." : `Employee created · ${res.data.employee_code}`);
            navigate(`/employees/${res.data.id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="px-6 sm:px-8 py-12 text-sm text-slate-500">Loading…</div>;
    }
    if (!can("employee:write")) {
        return <div className="px-6 sm:px-8 py-12 text-sm text-slate-500">You don&apos;t have permission to edit employee records.</div>;
    }

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="employee-form-page">
            <PageHeader
                eyebrow="Employee Operations"
                title={isEdit ? `Edit ${form.full_name || "Employee"}` : "Add New Employee"}
                description="Capture identity, employment, banking, address and emergency-contact details. Banking and statutory fields are required for Payroll later."
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => navigate(isEdit ? `/employees/${id}` : "/employees")} data-testid="employee-form-cancel">
                            <ArrowLeft size={14} className="mr-1" /> Cancel
                        </Button>
                        <Button onClick={submit} disabled={saving} data-testid="employee-form-save">
                            <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : isEdit ? "Save changes" : "Create employee"}
                        </Button>
                    </div>
                }
            />

            {!isEdit && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="legacy-shell-banner">
                    <p className="text-sm font-semibold text-amber-900">Employee-only shell creation</p>
                    <p className="mt-1 text-xs text-amber-800">
                        This legacy form creates an employee record <strong>without a user account</strong> — no ESS access
                        and no leave balances will be provisioned. Normal new hires who need a login must use{" "}
                        <Link to="/admin/onboard" data-testid="unified-onboarding-link" className="font-medium underline underline-offset-2">
                            Unified Onboarding
                        </Link>.
                    </p>
                    <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-amber-900">
                        <input
                            type="checkbox"
                            data-testid="employee-shell-ack-checkbox"
                            className="mt-0.5 h-4 w-4 rounded border-amber-300"
                            checked={ackNoEss}
                            onChange={(e) => setAckNoEss(e.target.checked)}
                        />
                        <span>Create employee without a user account — no ESS access and no leave balances will be provisioned.</span>
                    </label>
                </div>
            )}

            <div className="mt-6 space-y-6">
                <Section title="Identity">
                    <Field label="Full Name" required testId="ff-full_name">
                        <Input data-testid="employee-input-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
                    </Field>
                    <Field label="Personal Email" testId="ff-personal_email">
                        <Input data-testid="employee-input-personal-email" type="email" value={form.personal_email} onChange={(e) => set("personal_email", e.target.value)} />
                    </Field>
                    <Field label="Official Email" testId="ff-official_email">
                        <Input data-testid="employee-input-official-email" type="email" value={form.official_email} onChange={(e) => set("official_email", e.target.value)} />
                    </Field>
                    <Field label="Mobile" testId="ff-mobile">
                        <Input data-testid="employee-input-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} />
                    </Field>
                    <Field label="Alternate Mobile">
                        <Input value={form.alternate_mobile} onChange={(e) => set("alternate_mobile", e.target.value)} />
                    </Field>
                    <Field label="Date of Birth">
                        <Input type="date" value={form.dob || ""} onChange={(e) => set("dob", e.target.value)} />
                    </Field>
                    <Field label="Gender">
                        <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.gender} onChange={(e) => set("gender", e.target.value)}>
                            <option value="">— select —</option>
                            {(meta.genders || []).map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                    </Field>
                    <Field label="PAN" hint="10 chars: AAACA1234B">
                        <Input data-testid="employee-input-pan" value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} />
                    </Field>
                    <Field label="Aadhaar" hint="12 digits">
                        <Input value={form.aadhaar} onChange={(e) => set("aadhaar", e.target.value.replace(/\D/g, ""))} maxLength={12} />
                    </Field>
                </Section>

                <Section title="Employment">
                    <Field label="Employment Category" required>
                        <select data-testid="employee-input-category" className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.employment_category} onChange={(e) => set("employment_category", e.target.value)}>
                            {(meta.employment_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="Department">
                        <select data-testid="employee-input-department" className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.department} onChange={(e) => set("department", e.target.value)}>
                            <option value="">— select —</option>
                            {(meta.departments || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Designation">
                        <select data-testid="employee-input-designation" className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.designation} onChange={(e) => set("designation", e.target.value)}>
                            <option value="">— select —</option>
                            {(meta.designations || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Date of Joining">
                        <Input data-testid="employee-input-doj" type="date" value={form.date_of_joining || ""} onChange={(e) => set("date_of_joining", e.target.value)} />
                    </Field>
                    <Field label="Confirmation Date">
                        <Input type="date" value={form.confirmation_date || ""} onChange={(e) => set("confirmation_date", e.target.value)} />
                    </Field>
                    <Field label="Probation End Date">
                        <Input type="date" value={form.probation_end_date || ""} onChange={(e) => set("probation_end_date", e.target.value)} />
                    </Field>
                    <Field label="Notice Period (days)">
                        <Input type="number" min={0} max={365} value={form.notice_period_days} onChange={(e) => set("notice_period_days", e.target.value)} />
                    </Field>
                    <Field label="Status">
                        <select data-testid="employee-input-status" className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.status} onChange={(e) => set("status", e.target.value)}>
                            {(meta.employee_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Reporting Manager">
                        <select data-testid="employee-input-rm" className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.reporting_manager_employee_id} onChange={(e) => set("reporting_manager_employee_id", e.target.value)}>
                            <option value="">— none —</option>
                            {filteredManagers.map((m) => <option key={m.id} value={m.id}>{m.full_name} ({m.employee_code})</option>)}
                        </select>
                    </Field>
                    <Field label="Holiday Calendar">
                        <select data-testid="employee-input-hcal" className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.holiday_calendar_id || ""} onChange={(e) => set("holiday_calendar_id", e.target.value)}>
                            <option value="">— default for fiscal year —</option>
                            {holidayCalendars.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.code} · {c.fiscal_year})</option>)}
                        </select>
                    </Field>
                    {/* Iter 98 / Phase E2.4 — per-employee attendance source override. */}
                    <Field label="Attendance Source (override)" hint="Overrides the resolved AttendancePolicy mode. Use for individual WFH exceptions.">
                        <select data-testid="employee-input-source-override"
                                className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
                                value={form.attendance_source_override || ""}
                                onChange={(e) => set("attendance_source_override", e.target.value)}>
                            <option value="">— use policy default —</option>
                            <option value="manual">Manual ESS (web / biometric)</option>
                            <option value="desktop_agent">Desktop Agent (WFH)</option>
                            <option value="hybrid">Hybrid (agent + manual fallback)</option>
                        </select>
                    </Field>
                </Section>

                <Section title="Banking">
                    <Field label="Bank Name">
                        <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} />
                    </Field>
                    <Field label="Account Number">
                        <Input value={form.bank_account_number} onChange={(e) => set("bank_account_number", e.target.value)} />
                    </Field>
                    <Field label="IFSC Code" hint="11 chars: HDFC0001234">
                        <Input value={form.bank_ifsc} onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())} maxLength={11} />
                    </Field>
                    <Field label="Beneficiary Name">
                        <Input value={form.bank_beneficiary_name} onChange={(e) => set("bank_beneficiary_name", e.target.value)} />
                    </Field>
                </Section>

                <Section title="Address">
                    <div className="md:col-span-2 lg:col-span-3"><Field label="Current Address">
                        <Textarea rows={2} value={form.current_address} onChange={(e) => set("current_address", e.target.value)} />
                    </Field></div>
                    <div className="md:col-span-2 lg:col-span-3"><Field label="Permanent Address">
                        <Textarea rows={2} value={form.permanent_address} onChange={(e) => set("permanent_address", e.target.value)} />
                    </Field></div>
                </Section>

                <Section title="Emergency Contact">
                    <Field label="Name">
                        <Input value={form.emergency_contact.name} onChange={(e) => setEmergency("name", e.target.value)} />
                    </Field>
                    <Field label="Number">
                        <Input value={form.emergency_contact.number} onChange={(e) => setEmergency("number", e.target.value)} />
                    </Field>
                    <Field label="Relationship">
                        <Input value={form.emergency_contact.relationship} onChange={(e) => setEmergency("relationship", e.target.value)} placeholder="Father / Spouse / Sibling…" />
                    </Field>
                </Section>

                <Section title="Notes">
                    <div className="md:col-span-2 lg:col-span-3"><Field label="Internal Notes">
                        <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Any operational notes about this employee…" />
                    </Field></div>
                </Section>
            </div>
        </div>
    );
}
